import { useContext, useEffect, useState, useCallback } from 'react';
import {
	AlertTriangle,
	Bell,
	Calendar,
	Check,
	Clock,
	Gift,
	Info,
	Lock,
	Package,
	Pencil,
	Plus,
	Power,
	Send,
	ShoppingBag,
	Store,
	Tag,
	TrendingUp,
	DollarSign,
	BarChart3,
	Percent,
	Users
} from 'lucide-react';
import { GlobalContext } from '../context/GlobalContext';
import { unsafeAPI } from '../hooks/api';
import { Button } from '../shadcn/Button';
import { Dialog } from '../components/Dialog';
import { Checkbox } from '../shadcn/Checkbox';
import Input from '../components/Input';
import { ContainerSpinner } from '../components/Spinner';
import { StatCard } from '../components/StatCard';
import { TabBar } from '../components/TabBar';
import { API_BASE } from '../utils/constants';
import { format_currency_usd, format_date, format_date_time } from '../utils/formatters';

const PRODUCT_TYPE_OPTIONS = [
	{ value: 'phone', label: 'Teléfonos' },
	{ value: 'tablet', label: 'Tablets' },
	{ value: 'laptop', label: 'Laptops' },
	{ value: 'other', label: 'Otros' }
];

const MONTH_NAMES = [
	'', 'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
	'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'
];

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
interface PromoConfig {
	allow_promo_creation: boolean;
	promotion_fee_pct: number | null;
	max_active_promos: number;
	promotion_restriction_reason: string | null;
	override_promo_time_constrain: boolean;
	current_active_promos_count: number;
	can_create_promo: boolean;
	next_available_promo_date: string | null;
}

interface Promotion {
	label: string;
	description: string;
	initial_percentage: number;
	product_types: string[];
	promotion_window: { from: string; to: string };
	is_active: boolean;
	created_at: string;
	created_by: string;
	is_store_promo: boolean;
	promotion_fee: number;
	levels?: string[];
	min_price?: number;
	stores?: string[];
	payment_options?: { installments: number; factor?: number }[];
}

interface PromoMetrics {
	[label: string]: {
		devices_sold_count: number;
		total_amount_financed: number;
		total_promotion_fee_charged: number;
	};
}

interface Eligibility {
	eligible: boolean;
	reasons: string[];
}

interface AnniversaryDate {
	month: number;
	day: number;
}

interface PushNotification {
	id: string;
	title: string;
	body: string;
	status: 'scheduled' | 'in-progress' | 'sent' | 'completed';
	scheduled_date: string;
	recipient_count: number;
	template_id: number;
	promotion_label: string;
	created_at: string;
}

interface NotificationTemplate {
	id: number;
	title: string;
	body: string;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
function isWithinEditWindow(createdAt: string): boolean {
	if (!createdAt) return false;
	try {
		const created = new Date(createdAt);
		if (isNaN(created.getTime())) return false;
		const sixHoursMs = 6 * 60 * 60 * 1000;
		return Date.now() - created.getTime() < sixHoursMs;
	} catch {
		return false;
	}
}

function getPromoStatusBadge(promo: Promotion): { label: string; className: string } {
	if (!promo.is_active) return { label: 'Inactiva', className: 'bg-neutral-700/50 text-neutral-300' };
	const now = new Date();
	const from = new Date(promo.promotion_window?.from);
	const to = new Date(promo.promotion_window?.to);
	if (now < from) return { label: 'Próxima', className: 'bg-amber-500/20 text-amber-300' };
	if (now > to) return { label: 'Finalizada', className: 'bg-neutral-700/50 text-neutral-400' };
	return { label: 'Activa', className: 'bg-emerald-500/20 text-emerald-300' };
}

function getNotifStatusBadge(status: string): { label: string; className: string } {
	switch (status) {
		case 'scheduled':
			return { label: 'Programada', className: 'bg-blue-500/20 text-blue-300' };
		case 'in-progress':
			return { label: 'Enviando', className: 'bg-amber-500/20 text-amber-300' };
		case 'sent':
		case 'completed':
			return { label: 'Enviada', className: 'bg-emerald-500/20 text-emerald-300' };
		default:
			return { label: status, className: 'bg-neutral-700/50 text-neutral-300' };
	}
}

// ---------------------------------------------------------------------------
// Eligibility Gate
// ---------------------------------------------------------------------------
function EligibilityGate({ eligibility }: { eligibility: Eligibility }) {
	return (
		<div className="mx-auto flex max-w-lg flex-col items-center justify-center py-20 text-center">
			<div className="mb-6 flex size-20 items-center justify-center rounded-full bg-neutral-900 ring-1 ring-neutral-800">
				<Lock className="size-10 text-neutral-500" />
			</div>
			<h2 className="text-xl font-semibold text-neutral-100">Promociones no disponibles</h2>
			<p className="mt-2 text-sm text-neutral-400">
				Tu tienda aún no cumple con los requisitos para crear promociones.
			</p>
			<div className="mt-6 w-full space-y-2">
				{eligibility.reasons.map((reason, i) => (
					<div key={i} className="flex items-start gap-3 rounded-xl border border-neutral-800 bg-neutral-900/60 p-4 text-left">
						<Info className="mt-0.5 size-4 shrink-0 text-amber-400" />
						<p className="text-sm text-neutral-300">{reason}</p>
					</div>
				))}
			</div>
		</div>
	);
}

// ---------------------------------------------------------------------------
// Anniversary Banner
// ---------------------------------------------------------------------------
function AnniversaryBanner({ anniversary }: { anniversary: AnniversaryDate | null }) {
	if (!anniversary) return null;
	const now = new Date();
	const isAnniversaryMonth = now.getMonth() + 1 === anniversary.month;
	if (!isAnniversaryMonth) return null;

	return (
		<div className="relative overflow-hidden rounded-2xl border border-emerald-500/20 bg-emerald-500/[0.06] p-4">
			<div className="absolute -right-6 -top-6 size-24 rounded-full bg-emerald-500/[0.06]" />
			<div className="absolute -right-2 top-8 size-16 rounded-full bg-emerald-500/[0.04]" />
			<div className="relative flex items-start gap-3">
				<div className="flex size-9 shrink-0 items-center justify-center rounded-xl bg-emerald-500/10">
					<Gift className="size-[18px] text-emerald-400" />
				</div>
				<div>
					<p className="text-sm font-semibold text-emerald-300">¡Mes de aniversario!</p>
					<p className="mt-0.5 text-xs leading-relaxed text-emerald-300/70">
						Tus promociones creadas este mes no tienen costo de comisión.
					</p>
				</div>
			</div>
		</div>
	);
}

// ---------------------------------------------------------------------------
// Anniversary Card (set or display)
// ---------------------------------------------------------------------------
function AnniversaryCard({
	anniversary,
	userData,
	onSet
}: {
	anniversary: AnniversaryDate | null;
	userData: any;
	onSet: (d: AnniversaryDate) => void;
}) {
	const [editing, setEditing] = useState(false);
	const [month, setMonth] = useState(anniversary?.month ?? 1);
	const [day, setDay] = useState(anniversary?.day ?? 1);
	const [submitting, setSubmitting] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const handleSave = async () => {
		setSubmitting(true);
		setError(null);
		const result = await unsafeAPI(
			'PUT',
			`${API_BASE}/storeprofile/store_anniversary/`,
			{
				master_store_email: userData?.master_store?.id || userData?.email,
				anniversary_month: month,
				anniversary_day: day
			},
			userData,
			null
		);
		setSubmitting(false);
		if (result?.success) {
			onSet({ month, day });
			setEditing(false);
		} else {
			setError(result?.status || 'No se pudo guardar.');
		}
	};

	const tooltipContent = (
		<div className="group/tip relative">
			<Info className="size-3.5 cursor-help text-neutral-600 transition-colors hover:text-neutral-400" />
			<div className="pointer-events-none absolute bottom-full left-1/2 z-50 mb-2 w-64 -translate-x-1/2 rounded-xl border border-neutral-700 bg-neutral-800 p-3 opacity-0 shadow-xl transition-opacity group-hover/tip:opacity-100">
				<p className="text-xs leading-relaxed text-neutral-200">
					Durante el mes de aniversario de tu tienda, las promociones que crees no tendrán costo de comisión.
				</p>
				<div className="absolute left-1/2 top-full -translate-x-1/2 border-4 border-transparent border-t-neutral-700" />
			</div>
		</div>
	);

	if (anniversary && !editing) {
		return (
			<div className="group/card relative rounded-2xl bg-neutral-900/50 p-5 transition-all duration-300 hover:bg-neutral-900/70">
				<div className="absolute inset-0 rounded-2xl ring-1 ring-inset ring-white/[0.04] transition-all duration-300 group-hover/card:ring-white/[0.08]" />
				<div className="relative flex items-start gap-4">
					<div className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-pink-500 to-rose-600">
						<Gift className="size-5 text-white" />
					</div>
					<div className="min-w-0 flex-1">
						<div className="flex items-center gap-1.5">
							<p className="text-[11px] font-medium uppercase tracking-widest text-neutral-500">Aniversario</p>
							{tooltipContent}
						</div>
						<p className="mt-1 text-lg font-semibold text-neutral-100">
							{anniversary.day} de {MONTH_NAMES[anniversary.month]}
						</p>
					</div>
					<button
						onClick={() => { setMonth(anniversary.month); setDay(anniversary.day); setEditing(true); }}
						className="rounded-lg p-1.5 text-neutral-600 transition-colors hover:bg-white/5 hover:text-neutral-300"
					>
						<Pencil className="size-3.5" />
					</button>
				</div>
			</div>
		);
	}

	if (!anniversary && !editing) {
		return (
			<button
				onClick={() => setEditing(true)}
				className="flex w-full items-start gap-4 rounded-2xl border border-dashed border-neutral-800 bg-neutral-900/30 p-5 text-left transition-all duration-300 hover:border-neutral-700 hover:bg-neutral-900/50"
			>
				<div className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-neutral-800/80">
					<Gift className="size-5 text-neutral-500" />
				</div>
				<div className="min-w-0 flex-1">
					<div className="flex items-center gap-1.5">
						<p className="text-[11px] font-medium uppercase tracking-widest text-neutral-500">Aniversario</p>
						<div onClick={(e) => e.stopPropagation()}>
							{tooltipContent}
						</div>
					</div>
					<p className="mt-1 text-sm text-neutral-500">Sin definir — toca para configurar</p>
				</div>
			</button>
		);
	}

	return (
		<div className="relative rounded-2xl bg-neutral-900/50 p-5">
			<div className="absolute inset-0 rounded-2xl ring-1 ring-inset ring-primary-500/20" />
			<div className="relative mb-4 flex items-center gap-2">
				<div className="flex size-8 items-center justify-center rounded-lg bg-gradient-to-br from-pink-500 to-rose-600">
					<Gift className="size-4 text-white" />
				</div>
				<p className="text-xs font-medium uppercase tracking-widest text-neutral-400">Configurar aniversario</p>
				{tooltipContent}
			</div>
			<div className="relative flex items-end gap-3">
				<div className="flex-1">
					<label className="mb-1.5 block text-[11px] font-medium uppercase tracking-widest text-neutral-500">Día</label>
					<select
						value={day}
						onChange={(e) => setDay(Number(e.target.value))}
						className="h-10 w-full rounded-xl border border-neutral-800 bg-neutral-900/80 px-3 text-sm text-neutral-100 outline-none transition-colors focus:border-primary-500"
					>
						{Array.from({ length: 31 }, (_, i) => i + 1).map((d) => (
							<option key={d} value={d}>{d}</option>
						))}
					</select>
				</div>
				<div className="flex-1">
					<label className="mb-1.5 block text-[11px] font-medium uppercase tracking-widest text-neutral-500">Mes</label>
					<select
						value={month}
						onChange={(e) => setMonth(Number(e.target.value))}
						className="h-10 w-full rounded-xl border border-neutral-800 bg-neutral-900/80 px-3 text-sm text-neutral-100 outline-none transition-colors focus:border-primary-500"
					>
						{MONTH_NAMES.slice(1).map((name, i) => (
							<option key={i + 1} value={i + 1}>{name.charAt(0).toUpperCase() + name.slice(1)}</option>
						))}
					</select>
				</div>
				<Button variant="primary" className="!w-auto !py-2.5 text-xs" loading={submitting} onClick={handleSave}>
					Guardar
				</Button>
				<button
					onClick={() => { setEditing(false); setError(null); }}
					className="rounded-lg px-3 py-2.5 text-xs text-neutral-500 transition-colors hover:text-neutral-200"
				>
					Cancelar
				</button>
			</div>
			{error && (
				<p className="relative mt-2 text-xs text-red-400">{error}</p>
			)}
		</div>
	);
}

// ---------------------------------------------------------------------------
// Config Banner
// ---------------------------------------------------------------------------
function ConfigBanner({
	config,
	loading,
	anniversary,
	storeName,
	userData,
	onAnniversarySet
}: {
	config: PromoConfig | null;
	loading: boolean;
	anniversary: AnniversaryDate | null;
	storeName: string;
	userData: any;
	onAnniversarySet: (d: AnniversaryDate) => void;
}) {
	if (loading) {
		return (
			<div className="mb-8 space-y-4">
				<div className="grid animate-pulse gap-4 sm:grid-cols-2 lg:grid-cols-4">
					{[...Array(4)].map((_, i) => (
						<div key={i} className="h-[88px] rounded-2xl bg-neutral-900/50" />
					))}
				</div>
			</div>
		);
	}
	if (!config) return null;

	const feePct = config.promotion_fee_pct != null ? (config.promotion_fee_pct * 100).toFixed(1) : null;
	const feeValue = feePct != null
		? feePct === '0.0' ? 'Sin comisión' : `${feePct}%`
		: '—';
	const feeSubtitle = feePct != null && feePct !== '0.0' ? 'sobre monto financiado' : undefined;

	return (
		<div className="mb-8 space-y-4">
			{storeName && (
				<div className="flex items-center gap-3">
					<div className="flex size-8 items-center justify-center rounded-lg bg-primary-500/10">
						<Store className="size-4 text-primary-400" />
					</div>
					<p className="text-base font-semibold text-neutral-100">{storeName}</p>
				</div>
			)}

			<AnniversaryBanner anniversary={anniversary} />

			{!config.allow_promo_creation && (
				<div className="flex items-start gap-3 rounded-2xl border border-red-500/20 bg-red-500/[0.06] p-4">
					<div className="flex size-9 shrink-0 items-center justify-center rounded-xl bg-red-500/10">
						<AlertTriangle className="size-[18px] text-red-400" />
					</div>
					<div>
						<p className="text-sm font-semibold text-red-300">Creación de promociones deshabilitada</p>
						{config.promotion_restriction_reason && (
							<p className="mt-1 text-xs leading-relaxed text-red-300/70">{config.promotion_restriction_reason}</p>
						)}
					</div>
				</div>
			)}

			<div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
				<StatCard
					icon={Percent}
					accent="bg-gradient-to-br from-violet-500 to-purple-600"
					label="Comisión"
					value={feeValue}
					subtitle={feeSubtitle}
				/>
				<StatCard
					icon={Package}
					accent="bg-gradient-to-br from-sky-500 to-blue-600"
					label="Promos activas"
					value={`${config.current_active_promos_count} / ${config.max_active_promos}`}
					subtitle="permitidas"
				/>
				<StatCard
					icon={Clock}
					accent="bg-gradient-to-br from-amber-500 to-orange-600"
					label="Periodo de espera"
					value={config.override_promo_time_constrain ? 'Sin límite' : '30 días'}
					subtitle={config.override_promo_time_constrain ? undefined : 'entre promociones'}
				/>
				<StatCard
					icon={Calendar}
					accent="bg-gradient-to-br from-emerald-500 to-green-600"
					label="Próxima disponible"
					value={config.next_available_promo_date ? format_date(config.next_available_promo_date) : 'Ahora'}
					subtitle={config.next_available_promo_date ? undefined : 'puedes crear una'}
				/>
			</div>

			<AnniversaryCard anniversary={anniversary} userData={userData} onSet={onAnniversarySet} />

			{config.allow_promo_creation && !config.can_create_promo && (
				<div className="flex items-start gap-3 rounded-2xl border border-amber-500/20 bg-amber-500/[0.06] p-4 light:border-amber-400/40 light:bg-amber-50">
					<div className="flex size-9 shrink-0 items-center justify-center rounded-xl bg-amber-500/10 light:bg-amber-100">
						<Info className="size-[18px] text-amber-400 light:text-amber-600" />
					</div>
					<p className="text-sm text-amber-200/90 light:text-amber-900">
						{config.current_active_promos_count >= config.max_active_promos
							? `Has alcanzado el límite de ${config.max_active_promos} ${config.max_active_promos === 1 ? 'promoción activa' : 'promociones activas'}.`
							: config.next_available_promo_date
								? `Podrás crear tu próxima promoción a partir del ${format_date(config.next_available_promo_date)}.`
								: 'No puedes crear una promoción en este momento.'}
					</p>
				</div>
			)}
		</div>
	);
}

// ---------------------------------------------------------------------------
// Promo Card
// ---------------------------------------------------------------------------
function PromoCard({
	promo,
	onEdit,
	onDeactivate,
	onScheduleNotification,
	readonly
}: {
	promo: Promotion;
	onEdit?: (p: Promotion) => void;
	onDeactivate?: (p: Promotion) => void;
	onScheduleNotification?: (p: Promotion) => void;
	readonly?: boolean;
}) {
	const status = getPromoStatusBadge(promo);
	const editable = !readonly && promo.is_active && isWithinEditWindow(promo.created_at);

	return (
		<div className="flex flex-col justify-between rounded-xl border border-neutral-800 bg-neutral-900/60 p-5 transition-colors hover:border-neutral-700">
			<div>
				<div className="mb-3 flex items-start justify-between gap-2">
					<div className="flex items-center gap-2">
						<Tag className="size-4 shrink-0 text-primary-400" />
						<h3 className="font-semibold text-neutral-100">{promo.label}</h3>
					</div>
					<span className={`shrink-0 rounded-full px-2.5 py-0.5 text-xs font-medium ${status.className}`}>
						{status.label}
					</span>
				</div>

				<p className="mb-4 text-sm leading-relaxed text-neutral-400">{promo.description}</p>

				<div className="space-y-2 text-sm text-neutral-300">
					<div className="flex items-center gap-2">
						<Percent className="size-3.5 text-neutral-500" />
						<span>Inicial: {(promo.initial_percentage * 100).toFixed(0)}%</span>
					</div>
					{promo.payment_options && promo.payment_options.length > 0 && (
						<div className="flex items-center gap-2">
							<Package className="size-3.5 text-neutral-500" />
							<span>Cuotas: {promo.payment_options.map((o) => o.installments).join(', ')}</span>
						</div>
					)}
					<div className="flex items-center gap-2">
						<ShoppingBag className="size-3.5 text-neutral-500" />
						<span>
							{promo.product_types?.map((t) => PRODUCT_TYPE_OPTIONS.find((o) => o.value === t)?.label || t).join(', ')}
						</span>
					</div>
					<div className="flex items-center gap-2">
						<Calendar className="size-3.5 text-neutral-500" />
						<span>
							{format_date(promo.promotion_window?.from)} — {format_date(promo.promotion_window?.to)}
						</span>
					</div>
					{promo.promotion_fee != null && (
						<div className="flex items-center gap-2">
							<DollarSign className="size-3.5 text-neutral-500" />
							<span>Comisión: {(promo.promotion_fee * 100).toFixed(1)}%</span>
						</div>
					)}
				</div>
			</div>

			{!readonly && promo.is_active && (
				<div className="mt-4 flex flex-wrap gap-2 border-t border-neutral-800 pt-4">
					{editable && onEdit && (
						<Button variant="secondary" className="!w-auto !py-2 text-xs" onClick={() => onEdit(promo)}>
							<span className="flex items-center gap-1.5">
								<Pencil className="size-3.5" />
								Editar
							</span>
						</Button>
					)}
					{onScheduleNotification && (
						<Button variant="secondary" className="!w-auto !py-2 text-xs" onClick={() => onScheduleNotification(promo)}>
							<span className="flex items-center gap-1.5">
								<Bell className="size-3.5" />
								Notificar
							</span>
						</Button>
					)}
					{onDeactivate && (
						<Button
							variant="secondary"
							className="!w-auto !py-2 text-xs text-red-400 hover:!bg-red-500/10"
							onClick={() => onDeactivate(promo)}
						>
							<span className="flex items-center gap-1.5">
								<Power className="size-3.5" />
								Desactivar
							</span>
						</Button>
					)}
				</div>
			)}
		</div>
	);
}

// ---------------------------------------------------------------------------
// Create / Edit Dialog
// ---------------------------------------------------------------------------
function PromoFormDialog({
	editingPromo,
	feePct,
	country,
	userData,
	onClose,
	onSuccess
}: {
	editingPromo: Promotion | null;
	feePct: number | null;
	country: string;
	userData: any;
	onClose: () => void;
	onSuccess: () => void;
}) {
	const INITIAL_OPTIONS = [20, 25, 30];
	const INSTALLMENT_OPTIONS = [6, 8, 10];

	const isEdit = editingPromo != null;
	const [label, setLabel] = useState(editingPromo?.label || '');
	const [description, setDescription] = useState(editingPromo?.description || '');
	const [percentage, setPercentage] = useState(
		editingPromo ? editingPromo.initial_percentage * 100 : INITIAL_OPTIONS[0]
	);
	const [installments, setInstallments] = useState<number[]>(
		editingPromo?.payment_options?.map((o) => o.installments) || [6]
	);
	const [dateFrom, setDateFrom] = useState<string | undefined>(editingPromo?.promotion_window?.from?.split('T')[0]);
	const [dateTo, setDateTo] = useState<string | undefined>(editingPromo?.promotion_window?.to?.split('T')[0]);
	const [submitting, setSubmitting] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const toggleInstallment = (value: number) => {
		setInstallments((prev) => (prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value].sort((a, b) => a - b)));
	};

	const validate = (): string | null => {
		if (!label.trim()) return 'El código de la promoción es requerido.';
		if (!description.trim()) return 'La descripción es requerida.';
		if (installments.length === 0) return 'Selecciona al menos un número de cuotas.';
		if (!dateFrom || !dateTo) return 'Las fechas de inicio y fin son requeridas.';
		const from = new Date(dateFrom);
		const to = new Date(dateTo);
		if (to <= from) return 'La fecha de fin debe ser posterior a la de inicio.';
		const diffDays = (to.getTime() - from.getTime()) / (1000 * 60 * 60 * 24);
		if (diffDays > 7) return 'La promoción no puede durar más de 7 días.';
		return null;
	};

	const handleSubmit = async () => {
		const validationError = validate();
		if (validationError) {
			setError(validationError);
			return;
		}
		setError(null);
		setSubmitting(true);

		const payload: any = {
			country,
			label: label.trim(),
			description: description.trim(),
			initial_percentage: percentage / 100,
			product_types: ['phone'],
			promotion_window: {
				from: dateFrom,
				to: dateTo
			},
			payment_options: installments.map((i) => ({ installments: i }))
		};

		const verb = isEdit ? 'PUT' : 'POST';
		const result = await unsafeAPI(verb, `${API_BASE}/storeprofile/store_promotions/`, payload, userData, null);

		setSubmitting(false);
		if (result?.success) {
			onSuccess();
		} else {
			setError(result?.status || 'Ha ocurrido un error. Intente más tarde.');
		}
	};

	const feeDisplay = feePct != null ? (feePct * 100).toFixed(1) : null;

	return (
		<Dialog title={isEdit ? 'Editar Promoción' : 'Crear Promoción'} onClose={onClose} cerrarButton={false} disableOverlayClose className="!max-w-lg">
			<div className="space-y-5">
				{feeDisplay && feeDisplay !== '0.0' && (
					<div className="flex items-start gap-2 rounded-lg border border-primary-500/20 bg-primary-500/5 p-3">
						<Info className="mt-0.5 size-4 shrink-0 text-primary-400" />
						<p className="text-xs text-primary-300">
							Krece cobrará una comisión del {feeDisplay}% sobre el monto financiado con esta promoción.
						</p>
					</div>
				)}

				<Input
					placeholder="Ej: VERANO2026"
					floatingLabel="Código de promoción"
					value={label}
					onChange={setLabel}
					disabled={isEdit}
					clearable={!isEdit}
				/>

				<Input
					placeholder="Describe la promoción"
					floatingLabel="Descripción"
					value={description}
					onChange={setDescription}
				/>

				<div className="space-y-2">
					<label className="text-xs font-medium text-neutral-400">Inicial (porcentaje de pago adelantado)</label>
					<div className="flex gap-2">
						{INITIAL_OPTIONS.map((opt) => (
							<button
								key={opt}
								type="button"
								onClick={() => setPercentage(opt)}
								className={`flex-1 rounded-lg border px-3 py-2.5 text-sm font-medium transition-colors ${
									percentage === opt
										? 'border-primary-500 bg-primary-500 text-white'
										: 'border-neutral-700 bg-neutral-900 text-neutral-300 hover:border-neutral-600'
								}`}
							>
								{opt}%
							</button>
						))}
					</div>
				</div>

				<div className="space-y-2">
					<label className="text-xs font-medium text-neutral-400">Número de cuotas</label>
					<div className="flex flex-wrap gap-2">
						{INSTALLMENT_OPTIONS.map((opt) => (
							<label key={opt} className="flex cursor-pointer items-center gap-2 text-sm text-neutral-200">
								<Checkbox
									checked={installments.includes(opt)}
									onCheckedChange={() => toggleInstallment(opt)}
								/>
								{opt} cuotas
							</label>
						))}
					</div>
				</div>

				<div className="flex items-center gap-2 rounded-lg bg-neutral-900/80 px-3 py-2">
					<ShoppingBag className="size-4 shrink-0 text-neutral-500" />
					<span className="text-xs text-neutral-400">Disponible para: <strong className="text-neutral-300">Teléfonos</strong></span>
				</div>

				<div className="grid gap-3 sm:grid-cols-2">
					<div className="relative space-y-1">
						<label className="absolute -top-2 left-2 z-10 rounded bg-neutral-925 px-1 text-xs font-medium text-neutral-400">
							Fecha de inicio
						</label>
						<input
							type="date"
							value={dateFrom || ''}
							onChange={(e) => setDateFrom(e.target.value || undefined)}
							className="h-12 w-full rounded-lg border border-neutral-800 bg-neutral-925 px-3 text-sm text-neutral-100 outline-none transition-all focus:border-primary-500 focus:ring-2 focus:ring-primary-500 [&::-webkit-calendar-picker-indicator]:invert"
						/>
					</div>
					<div className="relative space-y-1">
						<label className="absolute -top-2 left-2 z-10 rounded bg-neutral-925 px-1 text-xs font-medium text-neutral-400">
							Fecha de fin
						</label>
						<input
							type="date"
							value={dateTo || ''}
							onChange={(e) => setDateTo(e.target.value || undefined)}
							className="h-12 w-full rounded-lg border border-neutral-800 bg-neutral-925 px-3 text-sm text-neutral-100 outline-none transition-all focus:border-primary-500 focus:ring-2 focus:ring-primary-500 [&::-webkit-calendar-picker-indicator]:invert"
						/>
					</div>
				</div>

				{error && (
					<div className="flex items-start gap-2 rounded-lg border border-red-500/30 bg-red-500/10 p-3">
						<AlertTriangle className="mt-0.5 size-4 shrink-0 text-red-400" />
						<p className="text-xs text-red-300">{error}</p>
					</div>
				)}

				<div className="flex justify-end gap-2 border-t border-neutral-800 pt-4">
					<Button variant="secondary" className="!w-auto" onClick={onClose}>
						Cancelar
					</Button>
					<Button variant="primary" className="!w-auto" loading={submitting} onClick={handleSubmit} type="submit">
						{isEdit ? 'Guardar cambios' : 'Crear promoción'}
					</Button>
				</div>
			</div>
		</Dialog>
	);
}

// ---------------------------------------------------------------------------
// Deactivate Confirmation Dialog
// ---------------------------------------------------------------------------
function DeactivateDialog({
	promo,
	country,
	userData,
	onClose,
	onSuccess
}: {
	promo: Promotion;
	country: string;
	userData: any;
	onClose: () => void;
	onSuccess: () => void;
}) {
	const [submitting, setSubmitting] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const handleConfirm = async () => {
		setSubmitting(true);
		setError(null);
		const result = await unsafeAPI(
			'DELETE',
			`${API_BASE}/storeprofile/store_promotions/`,
			{ country, label: promo.label },
			userData,
			null
		);
		setSubmitting(false);
		if (result?.success) {
			onSuccess();
		} else {
			setError(result?.status || 'Ha ocurrido un error.');
		}
	};

	return (
		<Dialog title="Desactivar Promoción" onClose={onClose} cerrarButton={false}>
			<div className="space-y-4">
				<p className="text-sm text-neutral-300">
					¿Estás seguro que deseas desactivar la promoción <strong className="text-neutral-100">{promo.label}</strong>?
					Esta acción no se puede deshacer.
				</p>
				{error && (
					<div className="rounded-lg border border-red-500/30 bg-red-500/10 p-3">
						<p className="text-xs text-red-300">{error}</p>
					</div>
				)}
				<div className="flex justify-end gap-2">
					<Button variant="secondary" className="!w-auto" onClick={onClose}>
						Cancelar
					</Button>
					<Button variant="destructive" className="!w-auto" loading={submitting} onClick={handleConfirm}>
						Desactivar
					</Button>
				</div>
			</div>
		</Dialog>
	);
}

// ---------------------------------------------------------------------------
// Schedule Push Notification Dialog
// ---------------------------------------------------------------------------
function ScheduleNotificationDialog({
	promo,
	templates,
	country,
	userData,
	onClose,
	onSuccess
}: {
	promo: Promotion;
	templates: NotificationTemplate[];
	country: string;
	userData: any;
	onClose: () => void;
	onSuccess: () => void;
}) {
	const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(templates[0]?.id ?? null);
	const [scheduledDate, setScheduledDate] = useState('');
	const [scheduledTime, setScheduledTime] = useState('14:00');
	const [submitting, setSubmitting] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [successData, setSuccessData] = useState<PushNotification | null>(null);

	const handleSubmit = async () => {
		if (!selectedTemplateId) {
			setError('Selecciona una plantilla.');
			return;
		}
		if (!scheduledDate) {
			setError('Selecciona una fecha de envío.');
			return;
		}
		const dateTime = `${scheduledDate}T${scheduledTime}:00Z`;
		if (new Date(dateTime) <= new Date()) {
			setError('La fecha debe ser en el futuro.');
			return;
		}

		setError(null);
		setSubmitting(true);
		const result = await unsafeAPI(
			'POST',
			`${API_BASE}/storeprofile/store_promotion_push_notifications/`,
			{
				country,
				promotion_label: promo.label,
				template_id: selectedTemplateId,
				scheduled_date: dateTime
			},
			userData,
			null
		);
		setSubmitting(false);

		if (result?.success) {
			setSuccessData(result.data);
		} else {
			setError(result?.status || 'Ha ocurrido un error.');
		}
	};

	if (successData) {
		return (
			<Dialog title="Notificación programada" onClose={() => { onClose(); onSuccess(); }} cerrarButton={false}>
				<div className="space-y-4">
					<div className="flex items-start gap-3 rounded-xl border border-emerald-500/30 bg-emerald-500/10 p-4">
						<Check className="mt-0.5 size-5 shrink-0 text-emerald-400" />
						<div>
							<p className="font-semibold text-emerald-300">Notificación programada exitosamente</p>
							<p className="mt-1 text-sm text-emerald-300/80">
								Se enviará a {successData.recipient_count} usuarios el {format_date_time(successData.scheduled_date)}.
							</p>
						</div>
					</div>
					<div className="rounded-xl border border-neutral-800 bg-neutral-900/60 p-4">
						<p className="text-sm font-medium text-neutral-200">{successData.title}</p>
						<p className="mt-1 text-sm text-neutral-400">{successData.body}</p>
					</div>
					<div className="flex justify-end">
						<Button variant="primary" className="!w-auto" onClick={() => { onClose(); onSuccess(); }}>
							Cerrar
						</Button>
					</div>
				</div>
			</Dialog>
		);
	}

	return (
		<Dialog
			title={`Notificar: ${promo.label}`}
			onClose={onClose}
			cerrarButton={false}
			disableOverlayClose
			className="!max-w-xl"
		>
			<div className="space-y-5">
				<div className="flex items-start gap-2 rounded-lg border border-primary-500/20 bg-primary-500/5 p-3">
					<Users className="mt-0.5 size-4 shrink-0 text-primary-400" />
					<p className="text-xs text-primary-300">
						La notificación se enviará a los clientes elegibles de tu tienda.
					</p>
				</div>

				<div className="space-y-2">
					<label className="text-xs font-medium text-neutral-400">Selecciona una plantilla</label>
					<div className="max-h-64 space-y-2 overflow-y-auto pr-1">
						{templates.map((tpl) => (
							<button
								key={tpl.id}
								type="button"
								onClick={() => setSelectedTemplateId(tpl.id)}
								className={`w-full rounded-lg border p-3 text-left transition-colors ${
									selectedTemplateId === tpl.id
										? 'border-primary-500 bg-primary-500/10'
										: 'border-neutral-800 bg-neutral-900/60 hover:border-neutral-700'
								}`}
							>
								<p className="text-sm font-medium text-neutral-200">{tpl.title}</p>
								<p className="mt-1 text-xs leading-relaxed text-neutral-400">{tpl.body}</p>
							</button>
						))}
					</div>
				</div>

				<div className="grid gap-3 sm:grid-cols-2">
					<div className="relative space-y-1">
						<label className="absolute -top-2 left-2 z-10 rounded bg-neutral-925 px-1 text-xs font-medium text-neutral-400">
							Fecha de envío
						</label>
						<input
							type="date"
							value={scheduledDate}
							onChange={(e) => setScheduledDate(e.target.value)}
							className="h-12 w-full rounded-lg border border-neutral-800 bg-neutral-925 px-3 text-sm text-neutral-100 outline-none transition-all focus:border-primary-500 focus:ring-2 focus:ring-primary-500 [&::-webkit-calendar-picker-indicator]:invert"
						/>
					</div>
					<div className="relative space-y-1">
						<label className="absolute -top-2 left-2 z-10 rounded bg-neutral-925 px-1 text-xs font-medium text-neutral-400">
							Hora de envío
						</label>
						<input
							type="time"
							value={scheduledTime}
							onChange={(e) => setScheduledTime(e.target.value)}
							className="h-12 w-full rounded-lg border border-neutral-800 bg-neutral-925 px-3 text-sm text-neutral-100 outline-none transition-all focus:border-primary-500 focus:ring-2 focus:ring-primary-500 [&::-webkit-calendar-picker-indicator]:invert"
						/>
					</div>
				</div>

				{error && (
					<div className="flex items-start gap-2 rounded-lg border border-red-500/30 bg-red-500/10 p-3">
						<AlertTriangle className="mt-0.5 size-4 shrink-0 text-red-400" />
						<p className="text-xs text-red-300">{error}</p>
					</div>
				)}

				<div className="flex justify-end gap-2 border-t border-neutral-800 pt-4">
					<Button variant="secondary" className="!w-auto" onClick={onClose}>
						Cancelar
					</Button>
					<Button variant="primary" className="!w-auto" loading={submitting} onClick={handleSubmit} type="submit">
						<span className="flex items-center gap-1.5">
							<Send className="size-3.5" />
							Programar envío
						</span>
					</Button>
				</div>
			</div>
		</Dialog>
	);
}

// ---------------------------------------------------------------------------
// Notifications Tab Content
// ---------------------------------------------------------------------------
function NotificationsContent({
	notifications,
	loading
}: {
	notifications: PushNotification[];
	loading: boolean;
}) {
	if (loading) return <ContainerSpinner size={2} />;

	if (notifications.length === 0) {
		return (
			<div className="flex flex-col items-center justify-center py-16 text-neutral-500">
				<Bell className="mb-3 size-10" />
				<p className="text-lg font-medium">Sin notificaciones</p>
				<p className="mt-1 text-sm">
					Programa notificaciones push desde tus promociones activas para llegar a más clientes.
				</p>
			</div>
		);
	}

	return (
		<div className="space-y-3">
			{notifications.map((notif) => {
				const badge = getNotifStatusBadge(notif.status);
				return (
					<div key={notif.id} className="flex items-start justify-between gap-4 rounded-xl border border-neutral-800 bg-neutral-900/60 p-4">
						<div className="min-w-0 flex-1">
							<div className="mb-1 flex items-center gap-2">
								<span className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-medium ${badge.className}`}>
									{badge.label}
								</span>
								<span className="truncate text-xs text-neutral-500">{notif.promotion_label}</span>
							</div>
							<p className="text-sm font-medium text-neutral-200">{notif.title}</p>
							<p className="mt-0.5 text-xs leading-relaxed text-neutral-400">{notif.body}</p>
							<div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-neutral-500">
								<span className="flex items-center gap-1">
									<Calendar className="size-3" />
									{format_date_time(notif.scheduled_date)}
								</span>
								<span className="flex items-center gap-1">
									<Users className="size-3" />
									{notif.recipient_count} destinatarios
								</span>
							</div>
						</div>
					</div>
				);
			})}
		</div>
	);
}

// ---------------------------------------------------------------------------
// Metrics Tab Content
// ---------------------------------------------------------------------------
function MetricsContent({ metrics, loading }: { metrics: PromoMetrics | null; loading: boolean }) {
	if (loading) return <ContainerSpinner size={2} />;
	if (!metrics || Object.keys(metrics).length === 0) {
		return (
			<div className="flex flex-col items-center justify-center py-16 text-neutral-500">
				<BarChart3 className="mb-3 size-10" />
				<p className="text-lg font-medium">Sin métricas disponibles</p>
				<p className="mt-1 text-sm">Las métricas aparecerán cuando se realicen ventas con tus promociones.</p>
			</div>
		);
	}

	const labels = Object.keys(metrics);
	const totals = labels.reduce(
		(acc, label) => ({
			devices: acc.devices + metrics[label].devices_sold_count,
			financed: acc.financed + metrics[label].total_amount_financed,
			fees: acc.fees + metrics[label].total_promotion_fee_charged
		}),
		{ devices: 0, financed: 0, fees: 0 }
	);

	return (
		<div className="space-y-6">
			<div className="grid gap-3 sm:grid-cols-3">
				<div className="rounded-xl border border-neutral-800 bg-neutral-900/60 p-5">
					<div className="flex items-center gap-2">
						<ShoppingBag className="size-5 text-emerald-400" />
						<p className="text-xs font-medium uppercase tracking-wider text-neutral-500">Dispositivos vendidos</p>
					</div>
					<p className="mt-2 text-3xl font-bold text-neutral-100">{totals.devices}</p>
				</div>
				<div className="rounded-xl border border-neutral-800 bg-neutral-900/60 p-5">
					<div className="flex items-center gap-2">
						<TrendingUp className="size-5 text-blue-400" />
						<p className="text-xs font-medium uppercase tracking-wider text-neutral-500">Monto financiado</p>
					</div>
					<p className="mt-2 text-3xl font-bold text-neutral-100">{format_currency_usd(totals.financed)}</p>
				</div>
				<div className="rounded-xl border border-neutral-800 bg-neutral-900/60 p-5">
					<div className="flex items-center gap-2">
						<DollarSign className="size-5 text-amber-400" />
						<p className="text-xs font-medium uppercase tracking-wider text-neutral-500">Comisión total</p>
					</div>
					<p className="mt-2 text-3xl font-bold text-neutral-100">{format_currency_usd(totals.fees)}</p>
				</div>
			</div>

			<div>
				<h3 className="mb-3 text-sm font-semibold text-neutral-300">Desglose por promoción</h3>
				<div className="overflow-hidden rounded-xl border border-neutral-800">
					<table className="w-full text-sm">
						<thead>
							<tr className="border-b border-neutral-800 bg-neutral-900/80">
								<th className="px-4 py-3 text-left font-medium text-neutral-400">Promoción</th>
								<th className="px-4 py-3 text-right font-medium text-neutral-400">Ventas</th>
								<th className="px-4 py-3 text-right font-medium text-neutral-400">Monto financiado</th>
								<th className="px-4 py-3 text-right font-medium text-neutral-400">Comisión</th>
							</tr>
						</thead>
						<tbody>
							{labels.map((label) => (
								<tr key={label} className="border-b border-neutral-800/50 last:border-0">
									<td className="px-4 py-3 font-medium text-neutral-200">{label}</td>
									<td className="px-4 py-3 text-right text-neutral-300">{metrics[label].devices_sold_count}</td>
									<td className="px-4 py-3 text-right text-neutral-300">
										{format_currency_usd(metrics[label].total_amount_financed)}
									</td>
									<td className="px-4 py-3 text-right text-neutral-300">
										{format_currency_usd(metrics[label].total_promotion_fee_charged)}
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			</div>
		</div>
	);
}

// ---------------------------------------------------------------------------
// Promo List (Active / History)
// ---------------------------------------------------------------------------
function PromoList({
	promos,
	loading,
	total,
	offset,
	batchSize,
	onPageChange,
	emptyIcon,
	emptyTitle,
	emptySubtitle,
	readonly,
	onEdit,
	onDeactivate,
	onScheduleNotification
}: {
	promos: Promotion[];
	loading: boolean;
	total: number;
	offset: number;
	batchSize: number;
	onPageChange: (newOffset: number) => void;
	emptyIcon: React.ReactNode;
	emptyTitle: string;
	emptySubtitle: string;
	readonly?: boolean;
	onEdit?: (p: Promotion) => void;
	onDeactivate?: (p: Promotion) => void;
	onScheduleNotification?: (p: Promotion) => void;
}) {
	if (loading) return <ContainerSpinner size={2} />;

	if (promos.length === 0) {
		return (
			<div className="flex flex-col items-center justify-center py-16 text-neutral-500">
				{emptyIcon}
				<p className="mt-3 text-lg font-medium">{emptyTitle}</p>
				<p className="mt-1 text-sm">{emptySubtitle}</p>
			</div>
		);
	}

	const totalPages = Math.max(1, Math.ceil(total / batchSize));
	const currentPage = Math.floor(offset / batchSize) + 1;

	return (
		<div className="space-y-4">
			<div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
				{promos.map((promo) => (
					<PromoCard
						key={promo.label}
						promo={promo}
						readonly={readonly}
						onEdit={onEdit}
						onDeactivate={onDeactivate}
						onScheduleNotification={onScheduleNotification}
					/>
				))}
			</div>

			{totalPages > 1 && (
				<div className="flex items-center justify-center gap-3 pt-4">
					<Button
						variant="secondary"
						className="!w-auto !py-2 text-xs"
						disabled={offset === 0}
						onClick={() => onPageChange(Math.max(0, offset - batchSize))}
					>
						Anterior
					</Button>
					<span className="text-sm text-neutral-400">
						Página {currentPage} de {totalPages}
					</span>
					<Button
						variant="secondary"
						className="!w-auto !py-2 text-xs"
						disabled={offset + batchSize >= total}
						onClick={() => onPageChange(offset + batchSize)}
					>
						Siguiente
					</Button>
				</div>
			)}
		</div>
	);
}

// ---------------------------------------------------------------------------
// Animated Tab Bar + Content
// ---------------------------------------------------------------------------
const TAB_ITEMS = [
	{ id: 'active', label: 'Activas', icon: Tag },
	{ id: 'history', label: 'Historial', icon: Clock },
	{ id: 'notifications', label: 'Notificaciones', icon: Bell },
	{ id: 'metrics', label: 'Métricas', icon: BarChart3 }
];

function TabsSection({
	activeTab,
	setActiveTab,
	config,
	onCreateClick,
	notificationCount,
	activePromos,
	activeLoading,
	activeTotal,
	activeOffset,
	fetchActivePromos,
	historyPromos,
	historyLoading,
	historyTotal,
	historyOffset,
	fetchHistoryPromos,
	notifications,
	notifLoading,
	metrics,
	metricsLoading,
	batchSize,
	onEdit,
	onDeactivate,
	onScheduleNotification
}: {
	activeTab: string;
	setActiveTab: (tab: string) => void;
	config: PromoConfig | null;
	onCreateClick: () => void;
	notificationCount: number;
	activePromos: Promotion[];
	activeLoading: boolean;
	activeTotal: number;
	activeOffset: number;
	fetchActivePromos: (o: number) => void;
	historyPromos: Promotion[];
	historyLoading: boolean;
	historyTotal: number;
	historyOffset: number;
	fetchHistoryPromos: (o: number) => void;
	notifications: PushNotification[];
	notifLoading: boolean;
	metrics: PromoMetrics | null;
	metricsLoading: boolean;
	batchSize: number;
	onEdit: (p: Promotion) => void;
	onDeactivate: (p: Promotion) => void;
	onScheduleNotification: (p: Promotion) => void;
}) {
	const tabs = TAB_ITEMS.map((t) => ({
		...t,
		badge: t.id === 'notifications' && notificationCount > 0 ? notificationCount : null
	}));

	return (
		<div className="w-full">
			<div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
				<TabBar
					tabs={tabs}
					activeTab={activeTab}
					onTabChange={setActiveTab}
					hideLabelsOnMobile
				/>

				<Button
					variant="primary"
					className="!w-auto"
					disabled={!config?.can_create_promo}
					onClick={onCreateClick}
				>
					<span className="flex items-center gap-1.5">
						<Plus className="size-4" />
						Crear promoción
					</span>
				</Button>
			</div>

			{activeTab === 'active' && (
				<PromoList
					promos={activePromos}
					loading={activeLoading}
					total={activeTotal}
					offset={activeOffset}
					batchSize={batchSize}
					onPageChange={(o) => fetchActivePromos(o)}
					emptyIcon={<Tag className="size-10" />}
					emptyTitle="Sin promociones activas"
					emptySubtitle="Crea tu primera promoción para empezar a atraer más clientes."
					onEdit={onEdit}
					onDeactivate={onDeactivate}
					onScheduleNotification={onScheduleNotification}
				/>
			)}

			{activeTab === 'history' && (
				<PromoList
					promos={historyPromos}
					loading={historyLoading}
					total={historyTotal}
					offset={historyOffset}
					batchSize={batchSize}
					onPageChange={(o) => fetchHistoryPromos(o)}
					emptyIcon={<Clock className="size-10" />}
					emptyTitle="Sin historial"
					emptySubtitle="Aquí aparecerán las promociones que hayas desactivado o que hayan finalizado."
					readonly
				/>
			)}

			{activeTab === 'notifications' && (
				<NotificationsContent notifications={notifications} loading={notifLoading} />
			)}

			{activeTab === 'metrics' && (
				<MetricsContent metrics={metrics} loading={metricsLoading} />
			)}
		</div>
	);
}

// ---------------------------------------------------------------------------
// Main Page
// ---------------------------------------------------------------------------
export default function MisPromociones() {
	const { userData } = useContext(GlobalContext);
	const country = userData?.country;

	// Eligibility
	const [eligibility, setEligibility] = useState<Eligibility | null>(null);

	// Anniversary
	const [anniversary, setAnniversary] = useState<AnniversaryDate | null>(null);

	// Config
	const [config, setConfig] = useState<PromoConfig | null>(null);
	const [configLoading, setConfigLoading] = useState(true);

	// Promos
	const [activePromos, setActivePromos] = useState<Promotion[]>([]);
	const [activeTotal, setActiveTotal] = useState(0);
	const [activeOffset, setActiveOffset] = useState(0);
	const [activeLoading, setActiveLoading] = useState(true);

	const [historyPromos, setHistoryPromos] = useState<Promotion[]>([]);
	const [historyTotal, setHistoryTotal] = useState(0);
	const [historyOffset, setHistoryOffset] = useState(0);
	const [historyLoading, setHistoryLoading] = useState(true);

	// Metrics
	const [metrics, setMetrics] = useState<PromoMetrics | null>(null);
	const [metricsLoading, setMetricsLoading] = useState(true);

	// Notifications
	const [notifications, setNotifications] = useState<PushNotification[]>([]);
	const [notifTemplates, setNotifTemplates] = useState<NotificationTemplate[]>([]);
	const [notifLoading, setNotifLoading] = useState(true);

	// Tab
	const [activeTab, setActiveTab] = useState('active');

	// Dialogs
	const [showCreateDialog, setShowCreateDialog] = useState(false);
	const [editingPromo, setEditingPromo] = useState<Promotion | null>(null);
	const [deactivatingPromo, setDeactivatingPromo] = useState<Promotion | null>(null);
	const [schedulingNotifPromo, setSchedulingNotifPromo] = useState<Promotion | null>(null);

	const BATCH_SIZE = 20;

	// ---- Fetchers ----

	const fetchEligibility = useCallback(async () => {
		if (!userData || !country) return;
		const result = await unsafeAPI(
			'GET',
			`${API_BASE}/storeprofile/store_promotions_tab_eligibility/?country=${country}`,
			null,
			userData,
			null
		);
		if (result?.success) setEligibility(result.data);
	}, [userData, country]);

	const fetchAnniversary = useCallback(async () => {
		if (!userData || !country) return;
		const result = await unsafeAPI(
			'GET',
			`${API_BASE}/storeprofile/store_anniversary/`,
			null,
			userData,
			null
		);
		if (result?.success) setAnniversary(result.data?.anniversary_date ?? null);
	}, [userData, country]);

	const fetchConfig = useCallback(async () => {
		if (!userData || !country) return;
		setConfigLoading(true);
		const result = await unsafeAPI(
			'GET',
			`${API_BASE}/storeprofile/store_promotions_config/?country=${country}`,
			null,
			userData,
			null
		);
		if (result?.success) setConfig(result.data);
		setConfigLoading(false);
	}, [userData, country]);

	const fetchActivePromos = useCallback(
		async (offset = 0) => {
			if (!userData || !country) return;
			setActiveLoading(true);
			const result = await unsafeAPI(
				'GET',
				`${API_BASE}/storeprofile/store_promotions/?country=${country}&is_active=true&batch_size=${BATCH_SIZE}&offset=${offset}`,
				null,
				userData,
				null
			);
			if (result?.success) {
				setActivePromos(result.data || []);
				setActiveTotal(result.total || 0);
				setActiveOffset(offset);
			}
			setActiveLoading(false);
		},
		[userData, country]
	);

	const fetchHistoryPromos = useCallback(
		async (offset = 0) => {
			if (!userData || !country) return;
			setHistoryLoading(true);
			const result = await unsafeAPI(
				'GET',
				`${API_BASE}/storeprofile/store_promotions/?country=${country}&is_active=false&batch_size=${BATCH_SIZE}&offset=${offset}`,
				null,
				userData,
				null
			);
			if (result?.success) {
				setHistoryPromos(result.data || []);
				setHistoryTotal(result.total || 0);
				setHistoryOffset(offset);
			}
			setHistoryLoading(false);
		},
		[userData, country]
	);

	const fetchMetrics = useCallback(async () => {
		if (!userData || !country) return;
		setMetricsLoading(true);
		const result = await unsafeAPI(
			'GET',
			`${API_BASE}/storeprofile/store_promotions_metrics/?country=${country}`,
			null,
			userData,
			null
		);
		if (result?.success) setMetrics(result.data || {});
		setMetricsLoading(false);
	}, [userData, country]);

	const fetchNotifications = useCallback(async () => {
		if (!userData || !country) return;
		setNotifLoading(true);
		const result = await unsafeAPI(
			'GET',
			`${API_BASE}/storeprofile/store_promotion_push_notifications/?country=${country}`,
			null,
			userData,
			null
		);
		if (result?.success) {
			setNotifications(result.data || []);
			if (result.templates) setNotifTemplates(result.templates);
		}
		setNotifLoading(false);
	}, [userData, country]);

	// ---- Initial load: fire everything in parallel ----

	useEffect(() => {
		fetchEligibility();
		fetchConfig();
		fetchAnniversary();
		fetchActivePromos(0);
		fetchHistoryPromos(0);
		fetchMetrics();
		fetchNotifications();
	}, [fetchEligibility, fetchConfig, fetchAnniversary, fetchActivePromos, fetchHistoryPromos, fetchMetrics, fetchNotifications]);

	const storeName = userData?.store_name || '';

	const refreshAll = () => {
		fetchConfig();
		fetchActivePromos(0);
		fetchHistoryPromos(0);
		fetchMetrics();
		fetchNotifications();
	};

	if (!userData?.email || !country) return null;

	// Skeleton while eligibility endpoint is loading
	if (!eligibility) {
		return (
			<div className="mx-auto w-full max-w-7xl p-4 md:p-6">
				<div className="mb-6">
					<div className="h-8 w-56 animate-pulse rounded-lg bg-neutral-800/60" />
					<div className="mt-2 h-4 w-80 animate-pulse rounded-md bg-neutral-800/40" />
				</div>
				<div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
					{[1, 2, 3, 4].map((i) => (
						<div key={i} className="flex flex-col gap-2 rounded-2xl border border-neutral-800/60 bg-neutral-900/40 p-4">
							<div className="size-9 animate-pulse rounded-xl bg-neutral-800/50" />
							<div className="h-3 w-20 animate-pulse rounded bg-neutral-800/40" />
							<div className="h-6 w-16 animate-pulse rounded bg-neutral-800/50" />
						</div>
					))}
				</div>
				<div className="mt-6 flex gap-2">
					{[1, 2, 3].map((i) => (
						<div key={i} className="h-9 w-28 animate-pulse rounded-lg bg-neutral-800/40" />
					))}
				</div>
				<div className="mt-4 space-y-2">
					{[1, 2, 3].map((i) => (
						<div key={i} className="flex items-center gap-3 rounded-xl px-3 py-3 ring-1 ring-inset ring-white/[0.04]">
							<div className="size-10 shrink-0 animate-pulse rounded-lg bg-neutral-800/50" />
							<div className="min-w-0 flex-1">
								<div className="h-4 w-3/4 animate-pulse rounded bg-neutral-800/50" />
								<div className="mt-1.5 h-3 w-1/2 animate-pulse rounded bg-neutral-800/30" />
							</div>
						</div>
					))}
				</div>
			</div>
		);
	}

	// Not eligible — show gate once eligibility has resolved
	if (!eligibility.eligible) {
		return (
			<div className="mx-auto w-full max-w-7xl p-4 md:p-6">
				<div className="mb-6">
					<h1 className="text-2xl font-semibold text-neutral-100">Mis Promociones</h1>
					<p className="mt-1 text-sm text-neutral-400">
						Administra las promociones de tu tienda, revisa métricas y consulta el historial.
					</p>
				</div>
				<EligibilityGate eligibility={eligibility} />
			</div>
		);
	}

	return (
		<div className="mx-auto w-full max-w-7xl p-4 md:p-6">
			<div className="mb-6">
				<h1 className="text-2xl font-semibold text-neutral-100">Mis Promociones</h1>
				<p className="mt-1 text-sm text-neutral-400">
					Administra las promociones de tu tienda, revisa métricas y consulta el historial.
				</p>
			</div>

			<ConfigBanner
				config={config}
				loading={configLoading}
				anniversary={anniversary}
				storeName={storeName}
				userData={userData}
				onAnniversarySet={(d) => { setAnniversary(d); fetchConfig(); }}
			/>

			<TabsSection
				activeTab={activeTab}
				setActiveTab={setActiveTab}
				config={config}
				onCreateClick={() => setShowCreateDialog(true)}
				notificationCount={notifications.length}
				activePromos={activePromos}
				activeLoading={activeLoading}
				activeTotal={activeTotal}
				activeOffset={activeOffset}
				fetchActivePromos={fetchActivePromos}
				historyPromos={historyPromos}
				historyLoading={historyLoading}
				historyTotal={historyTotal}
				historyOffset={historyOffset}
				fetchHistoryPromos={fetchHistoryPromos}
				notifications={notifications}
				notifLoading={notifLoading}
				metrics={metrics}
				metricsLoading={metricsLoading}
				batchSize={BATCH_SIZE}
				onEdit={(p) => setEditingPromo(p)}
				onDeactivate={(p) => setDeactivatingPromo(p)}
				onScheduleNotification={(p) => setSchedulingNotifPromo(p)}
			/>

			{showCreateDialog && (
				<PromoFormDialog
					editingPromo={null}
					feePct={config?.promotion_fee_pct ?? null}
					country={country}
					userData={userData}
					onClose={() => setShowCreateDialog(false)}
					onSuccess={() => {
						setShowCreateDialog(false);
						refreshAll();
					}}
				/>
			)}

			{editingPromo && (
				<PromoFormDialog
					editingPromo={editingPromo}
					feePct={config?.promotion_fee_pct ?? null}
					country={country}
					userData={userData}
					onClose={() => setEditingPromo(null)}
					onSuccess={() => {
						setEditingPromo(null);
						refreshAll();
					}}
				/>
			)}

			{deactivatingPromo && (
				<DeactivateDialog
					promo={deactivatingPromo}
					country={country}
					userData={userData}
					onClose={() => setDeactivatingPromo(null)}
					onSuccess={() => {
						setDeactivatingPromo(null);
						refreshAll();
					}}
				/>
			)}

			{schedulingNotifPromo && (
				notifTemplates.length > 0 ? (
					<ScheduleNotificationDialog
						promo={schedulingNotifPromo}
						templates={notifTemplates}
						country={country}
						userData={userData}
						onClose={() => setSchedulingNotifPromo(null)}
						onSuccess={() => {
							setSchedulingNotifPromo(null);
							refreshAll();
						}}
					/>
				) : (
					<Dialog title="Cargando plantillas..." onClose={() => setSchedulingNotifPromo(null)} cerrarButton={false}>
						<ContainerSpinner size={2} />
					</Dialog>
				)
			)}
		</div>
	);
}
